import { ResponseGetMerchflowStores, RouteGetMerchflowStores } from "@CommonApi/merchflow/stores";
import { RoutePostMerchflowUploadPsa } from "@CommonApi/merchflow/upload-psa";
import {
  PayloadPostMerchflows,
  ResponsePostMerchflows,
  RoutePostMerchflows,
} from "@CommonApi/merchflows";
import {
  PayloadPutMerchflows_merchflowId_Filters,
  ResponsePutMerchflows_merchflowId_Filters,
  RoutePutMerchflows_merchflowId_Filters,
} from "@CommonApi/merchflows/_reviewId_/filters";
import { ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import { RouteGetCategories } from "@CommonApi/categories";
import {
  PayloadPutMerchflowsFilterConfig_filterConfigId_,
  ResponsePutMerchflowsFilterConfig_filterConfigId_,
  RoutePutMerchflowsFilterConfig_filterConfigId_,
} from "@CommonApi/merchflows/filter-config/_filterConfigId_";
import {
  ParamsGetMerchflowsFilters,
  ResponseGetMerchflowsFilters,
  RouteGetMerchflowsFilters,
} from "@CommonApi/merchflows/filters";
import {
  ParamsGetMerchflowsVariants,
  ResponseGetMerchflowsVariants,
  RouteGetMerchflowsVariants,
} from "@CommonApi/merchflows/variants";
import { PayloadPostStoreSummary, RoutePostImportStoreSummary } from "@CommonApi/store-summary";
import {
  PayloadFormPostMerchflowsImportPlanograms,
  ResponsePostMerchflowsImportPlanograms,
  RoutePostMerchflowsImportPlanograms,
} from "@CommonApi/merchflows/import-planograms";
import {
  PayloadFormPostMerchflowsImportPlanogramsFromPsa,
  ResponsePostMerchflowsImportPlanogramsFromPsa,
  RoutePostMerchflowsImportPlanogramsFromPsa,
} from "@CommonApi/merchflows/import-planograms-from-psa";
import {
  QueryDeleteMerchflows_merchflowId_,
  ResponseDeleteMerchflows_merchflowId_,
  RouteDeleteMerchflows_merchflowId_,
} from "@CommonApi/merchflows/_merchflowId_/delete";
import {
  PayloadPostMerchflows_merchflowId_Duplicate,
  ResponsePostMerchflows_merchflowId_Duplicate,
  RoutePostMerchflows_merchflowId_Duplicate,
} from "@CommonApi/merchflows/_merchflowId_/duplicate";
import {
  PayloadFormPostMerchflowsUhd,
  ResponsePostMerchflowsUhd,
  RoutePostMerchflowsUhd,
} from "@CommonApi/merchflows/uhd";
import { useLoaders } from "src/components/InlineLoader";
import { useToasts } from "src/components/Toasts";
import {
  API,
  deleteRequest,
  getRequest,
  postFormDataRequest,
  postRequest,
  putRequest,
} from "src/utils/axios";
import { routeToUrl } from "@CommonUtils/routes";
import { getCategoriesReq } from "src/api";

const getMerchflowsFiltersReq = (queryParams: ParamsGetMerchflowsFilters) =>
  getRequest<ResponseGetMerchflowsFilters>(`${API}${RouteGetMerchflowsFilters}`, { queryParams });

const getMerchflowsVariantsReq = (queryParams: ParamsGetMerchflowsVariants) =>
  getRequest<ResponseGetMerchflowsVariants>(`${API}${RouteGetMerchflowsVariants}`, { queryParams });

const postMerchflowsReq = (payload: PayloadPostMerchflows) =>
  postRequest<ResponsePostMerchflows>(`${API}${RoutePostMerchflows}`, {
    payload,
  });

const putMerchflows_merchflowId_FiltersReq = (payload: PayloadPutMerchflows_merchflowId_Filters) =>
  putRequest<ResponsePutMerchflows_merchflowId_Filters>(
    `${API}${RoutePutMerchflows_merchflowId_Filters}`,
    {
      payload,
      params: {
        merchflowId: payload.flowId,
      },
    },
  );

const postMerchflowImportStoreSummaryReq = (payload: PayloadPostStoreSummary) => {
  return postFormDataRequest<string>(`${API}${RoutePostImportStoreSummary}`, {
    payload,
  });
};

const postMerchflowImportPlanogramsReq = (payload: PayloadFormPostMerchflowsImportPlanograms) => {
  return postFormDataRequest<ResponsePostMerchflowsImportPlanograms>(
    `${API}${RoutePostMerchflowsImportPlanograms}`,
    {
      payload,
    },
  );
};

const postMerchflowImportPlanogramsFromPsaReq = (
  payload: PayloadFormPostMerchflowsImportPlanogramsFromPsa,
) => {
  return postFormDataRequest<ResponsePostMerchflowsImportPlanogramsFromPsa>(
    `${API}${RoutePostMerchflowsImportPlanogramsFromPsa}`,
    {
      payload,
    },
  );
};

export const putMerchflowsFilterConfig_filterConfigIdReq = (
  filterConfigId: number,
  payload: PayloadPutMerchflowsFilterConfig_filterConfigId_,
) =>
  putRequest<ResponsePutMerchflowsFilterConfig_filterConfigId_>(
    `${API}${RoutePutMerchflowsFilterConfig_filterConfigId_}`,
    {
      payload,
      params: {
        filterConfigId,
      },
    },
  );

const deleteMerchflowReq = (merchflowId: number, confirmed: boolean) =>
  deleteRequest<ResponseDeleteMerchflows_merchflowId_>(
    routeToUrl(`${API}${RouteDeleteMerchflows_merchflowId_}`, { merchflowId }),
    {
      queryParams: confirmed
        ? ({
            confirmDeliveryHistoryLoss: "true",
          } satisfies QueryDeleteMerchflows_merchflowId_)
        : undefined,
    },
  );

const postDuplicateMerchflowReq = (
  merchflowId: number,
  payload: PayloadPostMerchflows_merchflowId_Duplicate,
) =>
  postRequest<ResponsePostMerchflows_merchflowId_Duplicate>(
    routeToUrl(`${API}${RoutePostMerchflows_merchflowId_Duplicate}`, { merchflowId }),
    { payload },
  );

const postMerchflowsUhdReq = (payload: PayloadFormPostMerchflowsUhd) => {
  const formPayload: Record<string, string | File> = {
    name: payload.name,
    notes: payload.notes ?? "",
    goLiveDate: payload.goLiveDate,
    coreRangeOption: payload.coreRangeOption,
    categoryCode: payload.categoryCode,
    retailerCategoryCode: payload.retailerCategoryCode,
  };
  if (payload.deactivationDate) formPayload.deactivationDate = payload.deactivationDate;
  if (payload.psaFile) formPayload.psaFile = payload.psaFile;
  return postFormDataRequest<ResponsePostMerchflowsUhd>(`${API}${RoutePostMerchflowsUhd}`, {
    payload: formPayload,
  });
};

export const useMerchflowsApi = () => {
  const { toast } = useToasts();
  const { addLoader, removeLoader } = useLoaders();

  const getMerchflowsCategoriesApi = async (): Promise<ResponseGetMerchflowsCategories | null> => {
    addLoader(RouteGetCategories);
    let response: ResponseGetMerchflowsCategories | null = null;

    try {
      const categories = (await getCategoriesReq()).data;
      response = Object.fromEntries(
        categories.map((g) => [g.categoryGroup, g.categories.map((c) => c.code)]),
      );
    } catch (error) {
      toast({
        title: "Failed to load categories",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RouteGetCategories);
    return response;
  };

  const getMerchflowsFiltersApi = async (queryParams: ParamsGetMerchflowsFilters) => {
    addLoader(RouteGetMerchflowsFilters);
    let response: ResponseGetMerchflowsFilters | null = null;

    try {
      response = (await getMerchflowsFiltersReq(queryParams)).data;
    } catch (error) {
      toast({
        title: "Failed to load filters",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RouteGetMerchflowsFilters);
    return response;
  };

  const getMerchflowsVariantsApi = async (queryParams: ParamsGetMerchflowsVariants) => {
    addLoader(RouteGetMerchflowsVariants);
    let response: ResponseGetMerchflowsVariants | null = null;

    try {
      response = (await getMerchflowsVariantsReq(queryParams)).data;
    } catch (error) {
      toast({
        title: "Failed to load merge rules",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RouteGetMerchflowsVariants);
    return response;
  };

  const postMerchflowsApi = async (payload: PayloadPostMerchflows) => {
    addLoader(RoutePostMerchflows);
    let response: ResponsePostMerchflows | null = null;

    try {
      response = (await postMerchflowsReq(payload)).data;
    } catch (error) {
      toast({
        title: "Failed to create new review",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RoutePostMerchflows);
    return response;
  };

  const putMerchflows_merchflowId_FiltersApi = async (
    payload: PayloadPutMerchflows_merchflowId_Filters,
  ) => {
    addLoader(RoutePutMerchflows_merchflowId_Filters);
    let response: ResponsePutMerchflows_merchflowId_Filters | null = null;

    try {
      response = (await putMerchflows_merchflowId_FiltersReq(payload)).data;
    } catch (error) {
      toast({
        title: "Failed to apply review filters",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RoutePutMerchflows_merchflowId_Filters);
    return response;
  };

  const putMerchflowsFilterConfig_filterConfigIdApi = async (
    filterConfigId: number,
    payload: PayloadPutMerchflowsFilterConfig_filterConfigId_,
  ) => {
    addLoader(RoutePutMerchflowsFilterConfig_filterConfigId_);
    let response: ResponsePutMerchflowsFilterConfig_filterConfigId_ | null = null;

    try {
      response = (await putMerchflowsFilterConfig_filterConfigIdReq(filterConfigId, payload)).data;
    } catch (error) {
      toast({
        title: "Failed to update filters",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RoutePutMerchflowsFilterConfig_filterConfigId_);
    return response;
  };

  const postMerchflowImportStoreSummaryApi = async (payload: PayloadPostStoreSummary) => {
    addLoader(RoutePostMerchflowUploadPsa);
    let response: string | null = null;

    try {
      response = (await postMerchflowImportStoreSummaryReq(payload)).data;
    } catch (error) {
      toast({
        title: "Failed to upload Store Summary file",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RoutePostMerchflowUploadPsa);
    return response;
  };

  const postMerchflowImportPlanogramsApi = async (
    payload: PayloadFormPostMerchflowsImportPlanograms,
  ) => {
    addLoader(RoutePostMerchflowsImportPlanograms);
    let response: ResponsePostMerchflowsImportPlanograms | null = null;

    try {
      response = (await postMerchflowImportPlanogramsReq(payload)).data;
    } catch (error) {
      toast({
        title: "Failed to import planograms",
        error,
      });
    }

    removeLoader(RoutePostMerchflowsImportPlanograms);
    return response;
  };

  const postMerchflowImportPlanogramsFromPsaApi = async (
    payload: PayloadFormPostMerchflowsImportPlanogramsFromPsa,
  ) => {
    addLoader(RoutePostMerchflowsImportPlanogramsFromPsa);
    let response: ResponsePostMerchflowsImportPlanogramsFromPsa | null = null;

    try {
      response = (await postMerchflowImportPlanogramsFromPsaReq(payload)).data;
    } catch (error) {
      toast({
        title: "Failed to import planograms from PSA",
        error,
      });
    }

    removeLoader(RoutePostMerchflowsImportPlanogramsFromPsa);
    return response;
  };

  const getMerchflowStoresReq = ({
    retailerCategoryCode,
    categoryCode,
  }: {
    retailerCategoryCode: string;
    categoryCode: string;
  }) =>
    getRequest<ResponseGetMerchflowStores>(`${API}${RouteGetMerchflowStores}`, {
      queryParams: {
        categoryCode,
        retailerCategoryCode,
      },
    });

  const getMerchflowStoresApi = async (params: {
    retailerCategoryCode: string;
    categoryCode: string;
  }) => {
    addLoader(RouteGetMerchflowStores);
    let response: ResponseGetMerchflowStores | null = null;

    try {
      response = (await getMerchflowStoresReq(params)).data;
    } catch (error) {
      toast({
        title: "Failed to get store summary data",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RouteGetMerchflowStores);
    return response;
  };

  const postMerchflowsUhdApi = async (
    payload: PayloadFormPostMerchflowsUhd,
  ): Promise<ResponsePostMerchflowsUhd | null> => {
    addLoader(RoutePostMerchflowsUhd);
    let response: ResponsePostMerchflowsUhd | null = null;

    try {
      response = (await postMerchflowsUhdReq(payload)).data;
    } catch (error) {
      toast({
        title: "Failed to create UHD flow",
        error: error instanceof Error ? error.message : String(error),
      });
    }

    removeLoader(RoutePostMerchflowsUhd);
    return response;
  };

  const deleteMerchflowApi = async (
    merchflowId: number,
    confirmed = false,
  ): Promise<ResponseDeleteMerchflows_merchflowId_ | null> => {
    addLoader(RouteDeleteMerchflows_merchflowId_);
    let response = null;

    try {
      response = (await deleteMerchflowReq(merchflowId, confirmed)).data;
    } catch (error) {
      toast({
        title: "Failed to delete merchflow",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RouteDeleteMerchflows_merchflowId_);
    return response;
  };

  const duplicateMerchflowApi = async (
    merchflowId: number,
    payload: PayloadPostMerchflows_merchflowId_Duplicate,
  ): Promise<ResponsePostMerchflows_merchflowId_Duplicate | null> => {
    addLoader(RoutePostMerchflows_merchflowId_Duplicate);
    let response = null;

    try {
      response = (await postDuplicateMerchflowReq(merchflowId, payload)).data;
    } catch (error) {
      toast({
        title: "Failed to duplicate merch flow",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RoutePostMerchflows_merchflowId_Duplicate);
    return response;
  };

  return {
    getMerchflowsCategoriesApi,
    getMerchflowsFiltersApi,
    getMerchflowsVariantsApi,
    postMerchflowsApi,
    postMerchflowsUhdApi,
    putMerchflows_merchflowId_FiltersApi,
    putMerchflowsFilterConfig_filterConfigIdApi,
    getMerchflowStoresApi,
    postMerchflowImportStoreSummaryApi,
    postMerchflowImportPlanogramsApi,
    postMerchflowImportPlanogramsFromPsaApi,
    deleteMerchflowApi,
    duplicateMerchflowApi,
  };
};
